
import { supabase } from '@/integrations/supabase/client';

export interface NotificationQueueItem {
  id: string;
  family_id: string;
  notification_type: string;
  activity_type: string;
  status: string;
  retry_count: number;
  created_at: string;
  processed_at?: string;
}

/**
 * Service for managing push notifications through the queue system
 */
export class NotificationService {
  /**
   * Get notification queue statistics for monitoring
   */
  static async getQueueStats(): Promise<{
    pending: number;
    processing: number;
    sent: number;
    failed: number;
  }> {
    try {
      const { data } = await supabase
        .from('notification_queue')
        .select('status')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()); // Last 24 hours
      
      const stats = {
        pending: 0,
        processing: 0,
        sent: 0,
        failed: 0,
      };
      
      data?.forEach(item => {
        stats[item.status as keyof typeof stats]++;
      });
      
      return stats;
    } catch (error) {
      console.error('Error fetching queue stats:', error);
      return { pending: 0, processing: 0, sent: 0, failed: 0 };
    }
  }

  /**
   * Get recent notification history for a family
   */
  static async getFamilyNotificationHistory(familyId: string, limit: number = 20): Promise<NotificationQueueItem[]> {
    try {
      const { data, error } = await supabase
        .from('notification_queue')
        .select('*')
        .eq('family_id', familyId)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching notification history:', error);
      return [];
    }
  }

  /**
   * Manually trigger queue processing via admin endpoint
   */
  static async triggerQueueProcessing(): Promise<boolean> {
    try {
      console.log('🔧 NotificationService: Triggering manual queue processing...');
      
      const { data, error } = await supabase.functions.invoke('admin-notifications', {
        body: { action: 'triggerProcessing' }
      });
      
      if (error) {
        console.error('🔧 NotificationService: Error triggering processing:', error);
        return false;
      }
      
      console.log('🔧 NotificationService: Processing triggered successfully:', data);
      return data?.success || false;
    } catch (error) {
      console.error('🔧 NotificationService: Exception triggering processing:', error);
      return false;
    }
  }

  /**
   * Get admin-specific queue statistics for all families
   */
  static async getAdminQueueStats(): Promise<{
    pending: number;
    processing: number;
    sent: number;
    failed: number;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('admin-notifications', {
        body: { action: 'getQueueStats' }
      });

      if (error) {
        console.error('Error calling admin notifications function:', error);
        throw error;
      }

      return data || { pending: 0, processing: 0, sent: 0, failed: 0 };
    } catch (error) {
      console.error('Error fetching admin queue stats:', error);
      return { pending: 0, processing: 0, sent: 0, failed: 0 };
    }
  }

  /**
   * Get admin notification history from all families
   */
  static async getAdminNotificationHistory(limit: number = 50): Promise<NotificationQueueItem[]> {
    try {
      const { data, error } = await supabase.functions.invoke('admin-notifications', {
        body: { action: 'getNotificationHistory', limit }
      });

      if (error) {
        console.error('Error calling admin notifications function:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching admin notification history:', error);
      return [];
    }
  }

  /**
   * Get admin push configuration (all active tokens across all users)
   */
  static async getAdminPushConfiguration(): Promise<{
    hasActiveTokens: boolean;
    tokenCount: number;
    queueHealth: 'healthy' | 'warning' | 'error';
    platform: string;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('admin-notifications', {
        body: { action: 'getPushConfiguration' }
      });

      if (error) {
        console.error('Error calling admin notifications function:', error);
        throw error;
      }

      return data || { hasActiveTokens: false, tokenCount: 0, queueHealth: 'error', platform: 'web' };
    } catch (error) {
      console.error('Error checking admin push configuration:', error);
      return { hasActiveTokens: false, tokenCount: 0, queueHealth: 'error', platform: 'web' };
    }
  }

  /**
   * Check if push notifications are properly configured
   */
  static async checkPushConfiguration(): Promise<{
    hasActiveTokens: boolean;
    tokenCount: number;
    queueHealth: 'healthy' | 'warning' | 'error';
    platform: string;
  }> {
    try {
      // Check for active push tokens
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        return { hasActiveTokens: false, tokenCount: 0, queueHealth: 'error', platform: 'web' };
      }

      const { data: tokens } = await supabase
        .from('push_tokens')
        .select('id')
        .eq('user_id', user.user.id)
        .eq('is_active', true);

      const tokenCount = tokens?.length || 0;

      // Check queue health
      const stats = await this.getQueueStats();
      const totalRecent = stats.pending + stats.processing + stats.sent + stats.failed;
      
      let queueHealth: 'healthy' | 'warning' | 'error' = 'healthy';
      if (stats.failed > stats.sent && totalRecent > 0) {
        queueHealth = 'error';
      } else if (stats.pending > 10) {
        queueHealth = 'warning';
      }

      return {
        hasActiveTokens: tokenCount > 0,
        tokenCount,
        queueHealth,
        platform: 'web'
      };
    } catch (error) {
      console.error('Error checking push configuration:', error);
      return { hasActiveTokens: false, tokenCount: 0, queueHealth: 'error', platform: 'web' };
    }
  }

  /**
   * Get push notification settings
   */
  static async getNotificationSettings(): Promise<{
    apnsConfigured: boolean;
    environment: 'development' | 'production' | 'unknown';
    lastTokenUpdate?: string;
  }> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) {
        return { apnsConfigured: false, environment: 'unknown' };
      }

      const { data: token } = await supabase
        .from('push_tokens')
        .select('device_info, last_used_at')
        .eq('user_id', user.user.id)
        .eq('is_active', true)
        .order('last_used_at', { ascending: false })
        .limit(1)
        .single();

      // Safely access device_info properties
      let environment: 'development' | 'production' | 'unknown' = 'unknown';
      if (token?.device_info && typeof token.device_info === 'object' && token.device_info !== null) {
        const deviceInfo = token.device_info as Record<string, any>;
        environment = deviceInfo.environment === 'development' || deviceInfo.environment === 'production' 
          ? deviceInfo.environment 
          : 'unknown';
      }
      
      return {
        apnsConfigured: !!token,
        environment: environment,
        lastTokenUpdate: token?.last_used_at
      };
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      return { apnsConfigured: false, environment: 'unknown' };
    }
  }
}
